/**
 * Authentication context and provider for global auth state management
 * Provides authentication state and methods to all child components
 *
 * @module AuthContext
 */

import { createContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { logger } from '../utils/logger'
import * as authService from '../services/authService'
import type { AuthState, LoginCredentials, ChangePasswordData, NewPasswordRequiredData } from '../types'

/**
 * Auth context value interface
 * Extends AuthState with action methods
 */
export interface AuthContextValue extends AuthState {
  signIn: (credentials: LoginCredentials) => Promise<void>
  signOut: () => Promise<void>
  changePassword: (data: ChangePasswordData) => Promise<void>
  completeNewPassword: (data: NewPasswordRequiredData) => Promise<void>
  isAdmin: () => Promise<boolean>
  refreshUser: () => Promise<void>
}

/**
 * Create auth context with undefined default
 * Will be provided by AuthProvider component
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication provider component
 * Wraps application to provide auth context to all children
 * Handles initial auth check on mount
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  })

  logger.debug(`AuthProvider: initializing provider with default state`)

  /**
   * Initialize authentication by checking for existing session
   */
  const initializeAuth = useCallback(async () => {
    logger.debug(`AuthProvider.initializeAuth: starting auth initialization`)
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const user = await authService.getCurrentUser()

      if (user) {
        logger.debug(`AuthProvider.initializeAuth: user session found`, { userId: user.id })
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        })
      } else {
        logger.debug(`AuthProvider.initializeAuth: no active session`)
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.initializeAuth: failed to initialize auth`, { error: errorMessage })
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      })
    }
  }, [])

  /**
   * Initialize auth on component mount
   */
  useEffect(() => {
    logger.debug(`AuthProvider: mount effect triggering auth initialization`)
    initializeAuth()
  }, [initializeAuth])

  /**
   * Handle user sign in
   */
  const handleSignIn = useCallback(async (credentials: LoginCredentials) => {
    logger.debug(`AuthProvider.handleSignIn: signing in user ${credentials.email}`)
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const user = await authService.signInUser(credentials)
      logger.info(`AuthProvider.handleSignIn: sign in successful for ${user.email}`)
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.handleSignIn: sign in failed`, { error: errorMessage })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [])

  /**
   * Handle user sign out
   */
  const handleSignOut = useCallback(async () => {
    logger.debug(`AuthProvider.handleSignOut: signing out user`)
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      await authService.signOutUser()
      logger.info(`AuthProvider.handleSignOut: sign out successful`)
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.handleSignOut: sign out failed`, { error: errorMessage })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [])

  /**
   * Handle password change for authenticated user
   */
  const handleChangePassword = useCallback(async (data: ChangePasswordData) => {
    logger.debug(`AuthProvider.handleChangePassword: changing password`)
    setState((prev) => ({ ...prev, error: null }))

    try {
      await authService.changeUserPassword(data)
      logger.info(`AuthProvider.handleChangePassword: password changed successfully`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.handleChangePassword: password change failed`, { error: errorMessage })
      setState((prev) => ({
        ...prev,
        error: errorMessage,
      }))
      throw error
    }
  }, [])

  /**
   * Handle completing new password requirement on first login
   */
  const handleCompleteNewPassword = useCallback(async (data: NewPasswordRequiredData) => {
    logger.debug(`AuthProvider.handleCompleteNewPassword: completing new password flow`)
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const user = await authService.completeNewPasswordRequired(data)
      logger.info(`AuthProvider.handleCompleteNewPassword: new password set for ${user.email}`)
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.handleCompleteNewPassword: failed`, { error: errorMessage })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [])

  /**
   * Check if current user is admin
   */
  const handleIsAdmin = useCallback(async (): Promise<boolean> => {
    logger.debug(`AuthProvider.handleIsAdmin: checking admin status`)
    const isUserAdmin = await authService.isAdmin()
    logger.debug(`AuthProvider.handleIsAdmin: result=${isUserAdmin}`)
    return isUserAdmin
  }, [])

  /**
   * Refresh current user data
   */
  const handleRefreshUser = useCallback(async () => {
    logger.debug(`AuthProvider.handleRefreshUser: refreshing user data`)
    setState((prev) => ({ ...prev, isLoading: true }))

    try {
      const user = await authService.getCurrentUser()

      if (user) {
        logger.debug(`AuthProvider.handleRefreshUser: user refreshed`, { userId: user.id })
        setState((prev) => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false,
        }))
      } else {
        logger.debug(`AuthProvider.handleRefreshUser: user session expired`)
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`AuthProvider.handleRefreshUser: refresh failed`, { error: errorMessage })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }, [])

  const value: AuthContextValue = {
    ...state,
    signIn: handleSignIn,
    signOut: handleSignOut,
    changePassword: handleChangePassword,
    completeNewPassword: handleCompleteNewPassword,
    isAdmin: handleIsAdmin,
    refreshUser: handleRefreshUser,
  }

  logger.debug(`AuthProvider: rendering with state`, {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    userId: state.user?.id,
  })

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
